import React, { useState, useEffect, useRef } from 'react';
import Loading from '../../Common/Loading';

import popup_bg1 from '../../Common/Images/ImagesForAlert/popup_bg1.png';
import alert_close_n from '../../Common/Images/ImagesForAlert/alert_close_n.png';
import alert_close_o from '../../Common/Images/ImagesForAlert/alert_close_o.png';
import submit_n from '../../Common/Images/submit_n.gif';
import clear_n from '../../Common/Images/clear_n.gif';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK || "https://sysadmin.elearnoncloud.com/";

interface Props {
    onClose: () => void;
    showWarning: (msg: string) => void;
    showConfirm: (msg: string, onConfirm: () => void) => void;
}

interface VoucherItem {
    VoucherTitle: string;
    VoucherName: string;
    VoucherValue: string;
    PurchasedOn: string;
    VoucherLink: string;
    VoucherKey: string;
    Status?: string;
}

const ImportVouchersPurchased: React.FC<Props> = ({ onClose, showWarning, showConfirm }) => {
    const [mode, setMode] = useState<'VIEW' | 'PREVIEW'>('VIEW');
    const [gridData, setGridData] = useState<VoucherItem[]>([]);
    const [voucherTitles, setVoucherTitles] = useState<string[]>([]);
    const [selectedTitle, setSelectedTitle] = useState<string>('0');
    const [loading, setLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        fetchTitles();
        fetchVouchers('0');
    }, []);

    const fetchTitles = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}api/vouchers/list_titles/`);
            if (res.ok) {
                const data = await res.json();
                setVoucherTitles(data);
            }
        } catch (error) {
            console.error("Error fetching titles", error);
        }
    };

    const fetchVouchers = async (titleFilter: string) => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/vouchers/?title=${titleFilter}`);
            if (res.ok) {
                const data = await res.json();
                setGridData(data);
                setMode('VIEW');
            }
        } catch (error) {
            console.error("Error fetching vouchers", error);
        } finally {
            setLoading(false);
        }
    };

    const handleUploadPreview = async () => {
        setErrorMessage('');
        setAlertMessage('');

        if (!fileInputRef.current?.files?.length) {
            showWarning("Select a file to import Purchased Vouchers List.");
            return;
        }

        const file = fileInputRef.current.files[0];
        const formData = new FormData();
        formData.append('file', file);

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/vouchers/preview_upload/`, {
                method: 'POST',
                body: formData
            });

            if (res.ok) {
                const data = await res.json();
                setGridData(data);
                setMode('PREVIEW');
                setAlertMessage(`Loaded ${data.length} records from file.`);
            } else {
                const err = await res.json();
                setErrorMessage("Error uploading file: " + (err.error || "Unknown error"));
            }
        } catch (error) {
            setErrorMessage("System error during upload.");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (mode !== 'PREVIEW') {
            showWarning(
                "Please upload and preview a file before saving."
            );
            return;
        }

        if (gridData.length === 0) {
            showWarning(
                "No records found to save."
            );
            return;
        }

        showConfirm(
            "Do you want to save these vouchers?",
            async () => {
                setLoading(true);
                setErrorMessage('');

                try {
                    const res = await fetch(
                        `${API_BASE_URL}api/vouchers/bulk_save/`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                vouchers: gridData
                            })
                        }
                    );

                    if (res.ok) {
                        const result = await res.json();

                        // VB AlertMesg
                        showWarning(
                            `${result.count} records inserted.`
                        );

                        // Clear selected file only
                        // DO NOT clear gridData
                        if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                        }

                        setAlertMessage('');
                        setErrorMessage('');

                        // VB:
                        // fn_EnableControls(True)
                        setMode('VIEW');

                        // IMPORTANT:
                        // VB calls DisplayVouchers() after save.
                        // Reload the table immediately from DB.
                        await fetchVouchers(selectedTitle);

                        // Refresh voucher title dropdown too,
                        // because a newly inserted VoucherTitle
                        // may need to appear there.
                        await fetchTitles();

                    } else {
                        let message =
                            `Failed to save records (HTTP ${res.status})`;

                        try {
                            const errorData = await res.json();

                            if (errorData?.error) {
                                message = errorData.error;
                            }
                        } catch {
                            // Keep default message
                        }

                        setErrorMessage(message);
                    }

                } catch (error) {
                    console.error(
                        "Error saving records:",
                        error
                    );

                    setErrorMessage(
                        error instanceof Error
                            ? `Error saving records: ${error.message}`
                            : "Error saving records."
                    );

                } finally {
                    setLoading(false);
                }
            }
        );
    };

    const handleClear = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        setAlertMessage('');
        setErrorMessage('');

        setGridData([]);
        setSelectedTitle('0');
        setMode('VIEW');
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setSelectedTitle(val);
        if (mode === 'VIEW') {
            fetchVouchers(val);
        }
    };

    const styles = {
        overlay: {
            position: 'fixed' as const,
            top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', justifyContent: 'center', alignItems: 'center'
        },
        modal: {
            backgroundColor: 'white',
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column' as const,
            border: 'none', overflow: 'hidden',
            fontFamily: 'Arial, sans-serif', fontSize: '9pt'
        },
        popupHeader: {
            backgroundImage: `url(${popup_bg1})`,
            backgroundRepeat: 'repeat-x',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            borderBottom: '1px solid #000', height: '30px',
            color: 'white', fontFamily: 'Calibri, Arial, sans-serif',
            fontSize: '14pt', fontWeight: 'bold' as const, paddingLeft: '10px'
        },
        mainContent: {
            padding: '10px',
            border: 'solid 1px #80a0ff',
            fontFamily: 'Arial',
            fontWeight: 'bold' as const,
            fontSize: '11pt',
            margin: '10px',
            marginBottom: '0px',
            width: 'calc(100% - 20px)'
        },
        pageCaption: {
            color: '#5B91B5',
            marginBottom: '5px',
            display: 'block'
        },
        helpText: {
            fontWeight: 'bold' as const,
            textAlign: 'left' as const,
            fontSize: '10pt',
            marginBottom: '10px'
        },
        link: {
            fontFamily: 'Arial',
            fontWeight: 'bold' as const,
            color: 'Red',
            marginLeft: '5px',
            textDecoration: 'underline',
        },
        uploadTable: {
            width: '100%',
            fontSize: '10pt',
            fontWeight: 'normal' as const
        },
        fileInput: {
            border: '1px solid #5B91B5',
            width: '365px',
            padding: '2px'
        },
        gridHeaderBar: {
            backgroundColor: '#ECFACB',
            padding: '4px',
            paddingLeft: '7px',
            fontFamily: 'Arial',
            fontSize: '11pt',
            fontWeight: 'bold' as const,
            marginTop: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start'
        },
        gridScrollArea: {
            flex: 1,
            overflow: 'auto',
            margin: '0 10px',
            border: '1px solid #80a0ff',
            marginBottom: '10px'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse' as const,
            fontFamily: 'Trebuchet MS',
            fontSize: '10pt'
        },
        th: {
            backgroundColor: '#C7DDFD',
            color: '#2859ac',
            fontWeight: 'normal' as const,
            padding: '8px',
            borderRight: '1px solid #80a0ff',
            textAlign: 'left' as const,
            position: 'sticky' as const,
            top: 0,
            fontSize: '10pt',
            whiteSpace: 'nowrap' as const
        },
        td: {
            padding: '8px',
            borderRight: '1px solid #80a0ff',
            verticalAlign: 'top' as const,
            color: 'black',
            fontSize: '10pt',
            whiteSpace: 'nowrap' as const
        },
        footer: {
            padding: '10px',
            textAlign: 'right' as const,
            borderTop: '1px solid #80a0ff',
            backgroundColor: '#F0F0F0'
        },
        actionImg: {
            cursor: 'pointer',
            marginLeft: '10px',
            verticalAlign: 'middle'
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>

                <div style={styles.popupHeader}>
                    Update Vouchers Purchased
                    <span>&nbsp;</span>
                    <img
                        src={alert_close_n}
                        alt="Close"
                        onClick={onClose}
                        style={{ cursor: 'pointer' }}
                        onMouseOver={(e) => e.currentTarget.src = alert_close_o}
                        onMouseOut={(e) => e.currentTarget.src = alert_close_n}
                    />
                </div>

                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div style={styles.mainContent}>
                        <span style={styles.pageCaption}>Update Vouchers List</span>

                        <div style={styles.helpText}>
                            If you require help with creating and working with *.xlsx and *.csv files:&nbsp;
                            <a href="#" style={styles.link} onClick={(e) => { e.preventDefault(); window.open('https://staging.elearnoncloud.com/School/Help/CSV_Tutorial.pdf', '_blank'); }}>Click Here</a>
                            <br />
                            Download an Excel template (vouchers list template.xlsx):&nbsp;
                            {/* <a href="#" style={styles.link} onClick={(e) => { e.preventDefault(); window.open('https://elearnoncloud.com/School/Help/Vouchers_List.xlsx', '_blank'); }}>Click Here</a> */}
                            <a
                                href="/School/Help/Vouchers_List.xlsx"
                                download="Vouchers_List.xlsx"
                                style={styles.link}
                            >
                                Click Here
                            </a>
                        </div>

                        <table style={styles.uploadTable}>
                            <tbody>
                                <tr>
                                    <td width="24%" valign="middle">Select *.csv file to import:</td>
                                    <td>
                                        <input
                                            type="file"
                                            accept=".csv"
                                            ref={fileInputRef}
                                            style={styles.fileInput}
                                            disabled={mode === 'PREVIEW'}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td>
                                        <button
                                            onClick={handleUploadPreview}
                                            disabled={mode === 'PREVIEW' || loading}
                                            style={{ marginTop: '5px', cursor: 'pointer' }}
                                        >
                                            Import Vouchers List
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        {alertMessage && <div style={{ color: 'blue', marginTop: '5px', fontSize: '10pt', fontWeight: 'normal' }}>{alertMessage}</div>}
                        {errorMessage && <div style={{ color: 'red', marginTop: '5px', fontSize: '10pt', fontWeight: 'normal' }}>{errorMessage}</div>}
                    </div>

                    <div style={{ margin: '0 10px', ...styles.gridHeaderBar }}>
                        <span style={{ marginRight: '10px' }}>Voucher Title</span>
                        <select
                            value={selectedTitle}
                            onChange={handleTitleChange}
                            style={{ width: '164px', fontFamily: 'Arial', fontSize: '10pt' }}
                            disabled={mode === 'PREVIEW'}
                        >
                            <option value="0">--ALL--</option>
                            {voucherTitles.map((t, i) => <option key={i} value={t}>{t}</option>)}
                        </select>
                    </div>

                    <div style={styles.gridScrollArea}>
                        {loading ? (
                            <Loading />
                        ) : (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>#</th>
                                        <th style={styles.th}>Title</th>
                                        <th style={styles.th}>Name</th>
                                        <th style={styles.th}>Value</th>
                                        <th style={styles.th}>Purchased On</th>
                                        <th style={styles.th}>Voucher Link</th>
                                        <th style={styles.th}>Voucher Key</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gridData.map((row, idx) => {
                                        const bg = idx % 2 === 0 ? 'white' : '#f4faff';

                                        return (
                                            <tr key={idx} style={{ backgroundColor: bg }}>

                                                {/* # Row Number */}
                                                <td style={styles.td}>
                                                    {idx + 1}
                                                </td>

                                                {/* Title */}
                                                <td style={styles.td}>
                                                    {row.VoucherTitle}
                                                </td>

                                                {/* Name */}
                                                <td style={styles.td}>
                                                    {row.VoucherName}
                                                </td>

                                                {/* Value */}
                                                <td style={styles.td}>
                                                    {row.VoucherValue}
                                                </td>

                                                {/* Purchased On */}
                                                <td style={styles.td}>
                                                    {row.PurchasedOn}
                                                </td>

                                                {/* Voucher Link */}
                                                <td style={styles.td}>
                                                    {row.Status === 'Incomplete Data'
                                                        ? <span style={{ color: 'red' }}>Incomplete Data</span>
                                                        : row.VoucherLink
                                                    }
                                                </td>

                                                {/* Voucher Key */}
                                                <td style={styles.td}>
                                                    {row.VoucherKey}
                                                </td>

                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div style={styles.footer}>
                        <img
                            src={submit_n}
                            alt="Save"
                            onClick={handleSave}
                            style={{
                                ...styles.actionImg,
                                cursor: 'pointer'
                            }}
                            title="Save Records"
                        />
                        <img
                            src={clear_n}
                            alt="Clear"
                            onClick={handleClear}
                            style={{
                                ...styles.actionImg,
                                cursor: 'pointer'
                            }}
                            title={mode === 'PREVIEW' ? "Clear / Cancel" : "Close"}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImportVouchersPurchased;